import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createProjectActions } from './actions.js';
import { useProjectListController } from './useProjectListController.js';
import { useSessionListEvents } from '../lists/useSessionListEvents.js';

export const useHostProjects = ({ api, options, actions, workspaceLocation, ready, enabled, publicationKey = '', onProjectionInvalidated }) => {
  const scopeKey = JSON.stringify([options.userId, options.instanceId,
    options.domainPackDomain, options.domainPackVersion]);
  const list = useProjectListController({ projectAPI: api.projectAPI, scopeKey, enabled });
  const [eventsUnavailable, setEventsUnavailable] = useState(false);
  const projectActions = useMemo(() => createProjectActions({
    projectAPI: api.projectAPI, projectId: workspaceLocation.effectiveProjectId,
    projectItems: list.items, defaultProjectName: options.defaultProjectName,
    loadProjectList: list.loadProjectList, onProjectSelect: workspaceLocation.selectProject,
    resetSession: actions.resetSession, setProjectItems: list.setItems,
  }), [actions, api.projectAPI, list.items, list.loadProjectList, list.setItems,
    options.defaultProjectName, workspaceLocation.effectiveProjectId, workspaceLocation.selectProject]);
  const loadProjectList = list.loadProjectList;
  const invalidateProjectList = list.invalidateProjectList;
  const observedPublication = useRef({ scopeKey, publicationKey });
  useEffect(() => {
    const previous = observedPublication.current;
    observedPublication.current = { scopeKey, publicationKey };
    if (!ready || !enabled || previous.scopeKey !== scopeKey || previous.publicationKey === publicationKey) return;
    void invalidateProjectList().catch(() => {});
  }, [enabled, invalidateProjectList, publicationKey, ready, scopeKey]);
  useEffect(() => setEventsUnavailable(false), [api.listEventsAPI]);
  // Only mutations, explicit commands and real invalidations may refresh lists.
  const invalidate = useCallback(() => Promise.all([actions.refreshSessionList(),
    loadProjectList({ force: true })]), [actions, loadProjectList]);
  const eventsAvailable = ready && Boolean(api.listEventsAPI?.subscribe) && !eventsUnavailable;
  useSessionListEvents({ enabled: eventsAvailable, listEventsAPI: api.listEventsAPI,
    onRefresh: invalidate, onProjectionInvalidated, onUnavailable: () => setEventsUnavailable(true) });
  return { ...projectActions, onRetryLoad: invalidate, projectItems: list.items, error: list.error,
    projectLoading: list.loading, projectLoadingMore: list.loadingMore, projectHasMore: list.window.hasMore,
    onLoadMoreProjects: list.loadMoreProjects, onSearchProjects: list.searchProjects,
  };
};
